import { useLookalikeSeeds } from '@aibees/elephant/components/company-similarity-search/hooks/use-lookalike-seeds';
import { PeopleSimilaritySearch } from '@aibees/elephant/components/people-similarity-search';
import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { SuggestionSearchBarOptionBase } from '@aibees/elephant/feature';
import { useCrawlRequestErrorContext } from '@aibees/elephant/feature/suggestion-search-bar/components/crawl-request/crawl-request-error-context';
import {
  SEARCH_PARAM_VALUE_KEY,
  useIsCrawlRequestFeatureEnabled,
  useSearchQueryFilter,
  useSearchSimilarityFromRoute,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { SearchType } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CrawlRequest } from '@aibees/elephant/feature/suggestion-search-bar/components/crawl-request';
import { useIsPeopleLookalikeEnabled } from '@aibees/elephant/hooks/people';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Stack, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { SimilaritySearchInput } from '../similarity-search-input';
import * as Styled from './styles';
import * as SX from './sx';
import type { DynamicSearchBarProps as Props } from './types';

export const DynamicSearchBar = memo(({ className, compact }: Props) => {
  const isPeopleLookalikeEnabled = useIsPeopleLookalikeEnabled();

  const isCrawlRequestFeatureEnabled = useIsCrawlRequestFeatureEnabled();

  const isSimilarity = useSearchSimilarityFromRoute();
  const type = useSearchTypeFromRoute();
  const searchKey = SEARCH_PARAM_VALUE_KEY;

  const { setParams, params, getParam, removeParam } = useSearchQueryFilter();

  const { track } = useTrackEvents();

  const defaultValues = useLookalikeSeeds();

  const dialogState = useBoolean();

  const dialogTitle = useMemo(() => {
    if (type === SearchType.Company) return 'AI Lookalike Company Search';
    if (type === SearchType.People) return 'AI Lookalike People Search';
    return 'AI Lookalike Search';
  }, [type]);

  const searchComponents = {
    [SearchType.Company]: (
      <Styled.CompanySearch renderMode="dialog" dialogState={dialogState} />
    ),
    [SearchType.People]: isPeopleLookalikeEnabled && (
      <PeopleSimilaritySearch
        compact={compact}
        renderMode="dialog"
        dialogState={dialogState}
      />
    ),
    // isSemantic: <CompanySemanticSearch />,
  };

  const conditionChecks = {
    isSimilarity,
    [SearchType.Company]: type === SearchType.Company,
    [SearchType.People]: type === SearchType.People,
  };
  const activeSearchBar =
    (
      Object.entries(searchComponents) as [
        keyof typeof searchComponents,
        JSX.Element
      ][]
    ).find(([key]) => conditionChecks[key])?.[1] || null;

  const handleSearch = (values: SuggestionSearchBarOptionBase[]) => {
    const searchValues: string[] = values.map((i) => i.value);

    // Page-level edits (e.g. removing a chip) only update the URL — the chips are
    // derived from it, so the removed seed can't be resurrected by a stale echo.
    setParams({
      ...params,
      ...{ [searchKey]: searchValues.join(FILTER_VALUE_SEPARATOR) },
    });

    if (type)
      track(type, {
        input: searchValues,
      });
  };
  const handleDelete = (
    linkedinUrl: string,
    values: SuggestionSearchBarOptionBase[]
  ) => {
    const value = getParam('value');
    if (value) {
      const draft = values.filter((i) => i.value !== linkedinUrl);
      handleSearch(draft);
      if (!draft.length) {
        removeParam(String(searchKey));
      }
    }
  };

  const { hasError } = useCrawlRequestErrorContext();

  if (type === SearchType.People && !isPeopleLookalikeEnabled) return null;

  return (
    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
      <Styled.SearchDialog
        open={dialogState.value}
        onClose={dialogState.onFalse}
        hasError={hasError}
        className={classnames({ isCrawlEnabled: isCrawlRequestFeatureEnabled })}
        maxWidth="smplus"
        fullWidth
      >
        <Styled.Container gap={1.5}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Box sx={SX.iconBox}>
                <Icon color="primary.500" name="fingerPrint" size={18} />
              </Box>
              <Typography variant="body2" color="grey.700">
                {dialogTitle}
              </Typography>
            </Stack>
            <Icon
              name="close"
              size={24}
              sxx={SX.close}
              onClick={dialogState.onFalse}
            />
          </Stack>
          {activeSearchBar}

          {isCrawlRequestFeatureEnabled && (
            <CrawlRequest
              onSubmit={dialogState.onFalse}
              searchCategory={type === SearchType.People ? 'people' : 'company'}
            />
          )}
        </Styled.Container>
      </Styled.SearchDialog>
      <Styled.Wrapper
        className={classnames('DynamicSearchBar', className)}
        sx={SX.root(compact)}
      >
        {compact && (
          <SimilaritySearchInput
            values={defaultValues}
            onDelete={(i, searchValue) => handleDelete(i.value!, searchValue)}
            addDialogState={dialogState}
          />
        )}
        {!compact && (
          <Stack>
            <Stack onClick={dialogState.onTrue} sx={SX.input}>
              <Icon
                sxx={SX.inputIcon}
                name={
                  type === SearchType.Company ? 'business' : 'linkedin-contain'
                }
                size={24}
              />
              <Typography
                variant="body2"
                fontWeight={400}
                color="grey.400"
                sx={SX.placeholder}
              >
                {type === SearchType.Company
                  ? 'e.g. Nike, nike.com, linkedin.com/company/nike'
                  : 'Enter Name or Paste the LinkedIn URL here to find similar profiles'}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Styled.Wrapper>
      {!compact && (
        <Button
          color="primary"
          variant="contained"
          size="medium"
          startIcon={<Icon name="outline-auto-awesome" size={16} />}
          onClick={dialogState.onTrue}
        >
          Search
        </Button>
      )}
    </Stack>
  );
});

DynamicSearchBar.displayName = 'DynamicSearchBar';
